/**
 * Boot-time decisions that used to be inert and now tear down the session.
 *
 * Before QMD-4332 a `LOGOUT` dispatched at mount only reset the reducers, so it was
 * harmless to fire it speculatively on a page nobody was signed in to. It now runs the
 * full teardown — purge, crypto-shred, reload — and the reloaded page evaluates the same
 * condition again. Anything that can be true on a logged-out load therefore has to be
 * guarded, or the app reloads forever.
 *
 * Both helpers are pure so the loop is testable without mounting `App`.
 */

/** Idle window after which a session found at mount is considered expired. */
export const MOUNT_INACTIVITY_LIMIT_MS = 1800000

/**
 * Should the mount-time inactivity check tear the session down?
 *
 * `lastUserAction` is written only while an admin is signed in (`AppLogout`), so its
 * absence means there is no session to expire. The original check subtracted the raw
 * `localStorage` value, and `now - null` coerces to `now` — always past the limit, so a
 * logged-out load always tore down.
 */
export const shouldLogOutForInactivity = (lastUserAction: string | null, now: number): boolean =>
  lastUserAction !== null && now - Number(lastUserAction) > MOUNT_INACTIVITY_LIMIT_MS

/**
 * Should the boot session check tear the session down?
 *
 * The check runs on every load, including the sign-in page, where there is nothing to
 * tear down and the resulting reload lands straight back here.
 */
export const shouldLogOutOnBoot = ({
  sessionSurvived,
  loggedIn,
}: {
  sessionSurvived: boolean
  loggedIn: boolean
}): boolean => !sessionSurvived && loggedIn
